import React, { useState } from "react";
import {
  Badge,
  IconButton,
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  useTheme,
  Chip,
} from "@mui/material";
import { NotificationsOutlined, CheckCircle, Cancel } from "@mui/icons-material";
import {
  useGetNotificationCountQuery,
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useApproveTaskUpdateMutation,
  useDeclineTaskUpdateMutation,
} from "state/api";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatDisplayDateTime } from "utils/datetime";

const NotificationMenu = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useSelector((state) => state.global.user);
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  // Lightweight count for badge (runs on every layout load)
  const { data: countData } = useGetNotificationCountQuery(undefined, { skip: !user });
  const unreadCountFromApi = countData?.unreadCount ?? 0;

  // Full list only when dropdown opens (lazy load - saves ~23KB per page load)
  const { data: notifications = [], refetch: refetchNotifications } = useGetNotificationsQuery(
    { unreadOnly: false },
    { skip: !user || !isOpen }
  );
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();
  const [approveTask] = useApproveTaskUpdateMutation();
  const [declineTask] = useDeclineTaskUpdateMutation();

  // Use API count when closed, computed when open (more accurate after actions)
  const unreadCount = isOpen ? notifications.filter((n) => !n.isRead).length : unreadCountFromApi;
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const isLead = user?.role === "lead";
  const canApprove = isAdmin || isLead;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markRead(notification._id);
      refetchNotifications();
    }
    handleClose();
    navigate("/notifications");
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    refetchNotifications();
  };

  const handleApprove = async (notification) => {
    if (!notification.taskId) return;
    try {
      // For now, approve the latest update (index 0)
      // In production, you might want to fetch the task to get the correct index
      await approveTask({ id: notification.taskId._id || notification.taskId, updateIndex: 0 });
      await markRead(notification._id);
      refetchNotifications();
    } catch (error) {
      console.error("Error approving task:", error);
      alert("Error approving: " + (error.data?.message || error.message));
    }
  };

  const handleDecline = async (notification) => {
    if (!notification.taskId) return;
    try {
      await declineTask({ id: notification.taskId._id || notification.taskId, updateIndex: 0 });
      await markRead(notification._id);
      refetchNotifications();
    } catch (error) {
      console.error("Error declining task:", error);
      alert("Error declining: " + (error.data?.message || error.message));
    }
  };


  return (
    <>
      <IconButton onClick={handleClick} sx={{ position: "relative" }}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsOutlined sx={{ fontSize: 25 }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            mt: 1.5,
          },
        }}
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              You're all caught up
            </Typography>
          </Box>
        ) : (
          <Box className="scrollbar-widget" sx={{ maxHeight: 400, overflow: "auto" }}>
            {notifications.map((notification) => (
              <Box
                key={notification._id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  backgroundColor: notification.isRead
                    ? "transparent"
                    : theme.palette.action.hover,
                  "&:hover": {
                    backgroundColor: theme.palette.action.selected,
                  },
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box flex={1}>
                    <Typography variant="body2" sx={{ fontWeight: notification.isRead ? "normal" : "bold" }}>
                      {notification.type === "task_update" && (
                        <>
                          Task Update: {notification.taskId?.title || "Task"}
                        </>
                      )}
                      {notification.type === "task_approved" && (
                        <>
                          ✅ Task Approved: {notification.taskId?.title || "Task"}
                        </>
                      )}
                      {notification.type === "task_declined" && (
                        <>
                          ❌ Task Declined: {notification.taskId?.title || "Task"}
                        </>
                      )}
                      {notification.type === "work_feedback" && (
                        <>
                          📝 Your work feedback for yesterday has been sent
                        </>
                      )}
                      {notification.type === "work_feedback_accepted" && (
                        <>
                          ✅ Feedback Accepted
                        </>
                      )}
                      {notification.type === "feedback_marked_done" && (
                        <>
                          ✅ Your feedback has been marked as done
                        </>
                      )}
                      {notification.type === "interview_schedule_submitted" && (
                        <>
                          📋 Interview Schedule Submitted
                        </>
                      )}
                      {notification.type === "interview_response_no" && (
                        <>
                          📞 Caller marked interview as No
                        </>
                      )}
                      {notification.type === "contact_form" && (
                        <>
                          📬 Contact: {notification.contactName || "Someone"}
                        </>
                      )}
                    </Typography>
                    {notification.type === "contact_form" && (
                      <>
                        {notification.contactEmail && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                            From: {notification.contactEmail}
                          </Typography>
                        )}
                        {notification.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block", whiteSpace: "pre-wrap" }}>
                            {notification.description}
                          </Typography>
                        )}
                      </>
                    )}
                    {notification.type !== "contact_form" && notification.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                        {notification.description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      {formatDisplayDateTime(notification.createdAt)}
                    </Typography>
                  </Box>
                  {!notification.isRead && (
                    <Chip label="New" size="small" color="primary" sx={{ ml: 1 }} />
                  )}
                </Box>

                {canApprove && notification.type === "task_update" && !notification.isRead && (
                  <Box display="flex" gap={1} mt={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(notification);
                      }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDecline(notification);
                      }}
                    >
                      Decline
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationMenu;
